import { useState, type RefObject } from "react";
import type { Editor } from "@tiptap/core";
import { Input } from "@/components/ui/input";
import type { BlockInfo, StyleProperty } from "./documentEditor";
import { withDefaultPxUnits } from "./styleLength";
import { keepsTextSession, TEXT_SESSION_HOLD_PROPS } from "./textSessionFocus";
import {
  computedSelectionStyle,
  readTextStyleSelection,
  type TextStyleSelection,
} from "./textStyleMark";
import { useRenderedStyle } from "./useRenderedStyle";

interface TextSizeInputProps {
  /** The open session's TipTap instance, or null when none is open. */
  editor: Editor | null;
  /** The selected block the box styles when no session is open. */
  block: BlockInfo | null;
  /** The preview container, measured for the size a block renders at. */
  previewRef: RefObject<HTMLDivElement>;
  /** Blocks editing (no target, or an upload is running). */
  disabled: boolean;
  /** Writes one inline-style property on the selected block. */
  onStyleChange: (property: StyleProperty, value: string) => void;
  /** Commits the session, for when focus leaves it from this input. */
  onCommitSession: () => void;
}

/** What the box shows for a selection whose runs disagree on their size. */
const MIXED_PLACEHOLDER = "–";

/** A typed edit, scoped to the target it was typed against. */
interface SizeDraft {
  target: string;
  value: string;
}

/**
 * The toolbar's font-size box, targeting whatever the author is on: with a
 * text session open it sizes the selected runs (their own size when the runs
 * agree on one, otherwise the size inherited from the block, and clearing it
 * hands them back to the block); with only a block selected it shows the size
 * the block actually renders at - authored style, `size` container attribute,
 * or computed - and writes the block's font-size style, which also drops the
 * `size` attribute so one channel remains. Always a bare number. Nothing is
 * written unless something was actually typed.
 *
 * Unlike the toggles beside it the box needs real focus to be typed into, so
 * in a session it holds the session open while it has focus (the session
 * commits on the editor's blur) and either hands focus back on Enter or, when
 * focus leaves the toolbar entirely, commits the session itself.
 * @param editor - The open session's TipTap instance, or null
 * @param block - The selected block styled when no session is open
 * @param previewRef - The preview container, measured for the rendered size
 * @param disabled - Blocks editing
 * @param onStyleChange - Writes one inline-style property on the block
 * @param onCommitSession - Commits the session
 * @returns The font-size box
 */
export function TextSizeInput({
  editor,
  block,
  previewRef,
  disabled,
  onStyleChange,
  onCommitSession,
}: TextSizeInputProps) {
  const target = editor !== null ? "session" : (block?.nodeId ?? "none");
  const [draftState, setDraftState] = useState<SizeDraft | null>(null);
  const draft = draftState?.target === target ? draftState.value : null;
  const selection =
    editor === null ? null : readTextStyleSelection(editor, "size");
  const mixed = selection?.state === "mixed";
  const authoredSize = block?.styles.fontSize ?? "";
  const containerSize = block?.containerSize ?? "";
  const renderedSize = useRenderedStyle(
    previewRef,
    editor === null ? (block?.nodeId ?? null) : null,
    "font-size",
    `${authoredSize}|${containerSize}`,
  );
  const shown =
    editor !== null
      ? effectiveSize(editor, selection)
      : bareSize(authoredSize || containerSize || renderedSize);

  const applyBlockSize = (typed: string) => {
    const trimmed = typed.trim();
    if (trimmed === shown) {
      return;
    }
    onStyleChange("fontSize", withDefaultPxUnits(trimmed));
  };

  return (
    <Input
      {...TEXT_SESSION_HOLD_PROPS}
      className="h-8 w-12 shrink-0 px-1.5 text-center text-xs"
      aria-label="Font size"
      title="Font size"
      placeholder={mixed ? MIXED_PLACEHOLDER : "Size"}
      disabled={disabled}
      value={draft ?? shown}
      onMouseDown={(event) => event.stopPropagation()}
      onChange={(event) => setDraftState({ target, value: event.target.value })}
      onKeyDown={(event) => {
        if (event.key !== "Enter") {
          return;
        }
        event.preventDefault();
        if (editor === null) {
          event.currentTarget.blur();
          return;
        }
        if (draft !== null) {
          applySize(editor, draft, true);
        }
        setDraftState(null);
        editor.commands.focus();
      }}
      onBlur={(event) => {
        const typed = draft;
        setDraftState(null);
        if (editor === null) {
          if (typed !== null && block !== null) {
            applyBlockSize(typed);
          }
          return;
        }
        const staying = keepsTextSession(editor, event.relatedTarget);
        if (typed !== null) {
          applySize(editor, typed, staying);
        }
        if (!staying) {
          onCommitSession();
        }
      }}
    />
  );
}

/**
 * The size the box shows: the selection's own size when its runs agree on one,
 * otherwise the size the browser paints it at.
 * @param editor - The open session's TipTap instance
 * @param selection - What the size attribute reads as across the selection
 * @returns The size as a bare number, or "" when it can't be measured
 */
function effectiveSize(
  editor: Editor,
  selection: TextStyleSelection | null,
): string {
  if (selection === null || selection.state === "mixed") {
    return "";
  }
  return bareNumber(
    selection.value ?? computedSelectionStyle(editor, "font-size"),
  );
}

/**
 * Strips the unit off a CSS length, rounded to one decimal.
 * @param length - The CSS length, e.g. "11.5px"
 * @returns The bare number, or "" when the length has none
 */
function bareNumber(length: string): string {
  const parsed = Number.parseFloat(length);
  return Number.isFinite(parsed) ? `${Math.round(parsed * 10) / 10}` : "";
}

/**
 * Strips the unit off a pixel size so the box reads as a bare number, leaving
 * any other unit verbatim for the author to see what they authored.
 * @param size - The effective CSS size ("" when there is none)
 * @returns The box's display value
 */
function bareSize(size: string): string {
  const trimmed = size.trim();
  const pixels = /^(\d+(?:\.\d+)?)px$/.exec(trimmed);
  return pixels ? pixels[1] : trimmed;
}

/**
 * Sizes the selection from what was typed, leaving it alone when the box holds
 * something that is not a positive number.
 * @param editor - The open session's TipTap instance
 * @param typed - The raw box contents
 * @param focus - Whether to hand focus back to the session
 */
function applySize(editor: Editor, typed: string, focus: boolean): void {
  const trimmed = typed.trim();
  const next = trimmed === "" ? null : pixelSize(trimmed);
  if (next === undefined) {
    return;
  }
  const selection = readTextStyleSelection(editor, "size");
  if (selection.state === "uniform" && selection.value === next) {
    return;
  }
  const chain = editor.chain();
  (focus ? chain.focus() : chain).setTextStyleSize(next).run();
}

/**
 * The pixel length a typed number stands for.
 * @param typed - The trimmed box contents
 * @returns The pixel length, or undefined when it is not a positive number
 */
function pixelSize(typed: string): string | undefined {
  const parsed = Number(typed);
  return Number.isFinite(parsed) && parsed > 0 ? `${parsed}px` : undefined;
}
